import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { Link, useLocation } from 'wouter';

import { navigation } from '@/constants/navigation';
import { useAuth } from '@/contexts/auth-context';
import { cn } from '@/lib/cn';

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const [location] = useLocation();
  const { license, user } = useAuth();
  const featureByHref: Record<string, string> = {
    '/usuarios': 'users',
    '/estoque': 'inventory',
    '/compras': 'purchases',
    '/motoboys': 'finance',
    '/historico-de-compras': 'purchases',
    '/lista-de-compras': 'shoppingLists',
    '/produtos': 'products',
    '/ficha-tecnica': 'recipes',
    '/operacoes-em-lote': 'finance',
    '/financeiro': 'finance',
    '/dre': 'dre',
    '/cmv': 'cmv',
    '/analises': 'analytics',
    '/sanitario': 'sanitary',
  };
  const permissionByHref: Record<string, string> = {
    '/usuarios': 'users.view',
    '/estoque': 'inventory.view',
    '/compras': 'purchases.view',
    '/motoboys': 'finance.view',
    '/historico-de-compras': 'purchases.view',
    '/lista-de-compras': 'shoppingLists.view',
    '/produtos': 'products.view',
    '/ficha-tecnica': 'recipes.view',
    '/operacoes-em-lote': 'finance.view',
    '/financeiro': 'finance.view',
    '/dre': 'dre.view',
    '/cmv': 'finance.view',
    '/analises': 'analytics.view',
    '/sanitario': 'sanitary.view',
  };
  const hasPermission = (permission: string) => {
    if (user?.role === 'OWNER' || user?.role === 'ADMIN') return true;
    if (!user?.tenantRoleId) return false;
    return user.permissions?.[permission] === true || user.permissions?.['*'] === true;
  };
  const visibleNavigation = navigation
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.ownerOnly && user?.role !== 'OWNER') return false;
        const match = Object.entries(featureByHref).find(
          ([href]) => item.href === href || item.href.startsWith(`${href}/`),
        );
        const permissionMatch = Object.entries(permissionByHref).find(
          ([href]) => item.href === href || item.href.startsWith(`${href}/`),
        );
        return (
          (!match || license?.features[match[1]] !== false) &&
          (!permissionMatch || hasPermission(permissionMatch[1]))
        );
      }),
    }))
    .filter((group) => group.items.length > 0);
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center gap-3 border-b px-5">
        <img
          src="/brand/chefiapro-mark.svg"
          alt=""
          aria-hidden="true"
          className="size-10 shrink-0 object-contain drop-shadow-sm"
        />
        <div>
          <p className="text-[15px] font-bold leading-tight tracking-tight">ChefiaPro</p>
          <p className="text-[11px] text-[rgb(var(--muted-foreground))]">
            Inteligência para restaurantes
          </p>
        </div>
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-5" aria-label="Navegação principal">
        {visibleNavigation.map((group) => (
          <div key={group.label}>
            <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.14em] text-[rgb(var(--muted-foreground))]">
              {group.label}
            </p>
            <div className="space-y-1">
              {group.items.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={onNavigate}
                  className={cn(
                    'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-[rgb(var(--muted-foreground))] transition-colors hover:bg-[rgb(var(--muted))] hover:text-[rgb(var(--foreground))]',
                    location === item.href &&
                      'bg-brand-50 text-brand-800 dark:bg-brand-950/60 dark:text-brand-200',
                  )}
                >
                  <item.icon className="size-[18px]" aria-hidden="true" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>
    </div>
  );
}

export function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <>
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r bg-[rgb(var(--card))]/95 backdrop-blur-xl lg:block">
        <SidebarContent />
      </aside>
      <AnimatePresence>
        {open ? (
          <>
            <motion.button
              type="button"
              className="fixed inset-0 z-40 bg-slate-950/55 backdrop-blur-sm lg:hidden"
              aria-label="Fechar menu"
              onClick={onClose}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <motion.aside
              className="fixed inset-y-0 left-0 z-50 w-[min(18rem,88vw)] border-r bg-[rgb(var(--card))] shadow-2xl lg:hidden"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 340, damping: 34 }}
            >
              <button
                type="button"
                className="absolute right-3 top-3 z-10 rounded-lg p-2 text-[rgb(var(--muted-foreground))] hover:bg-[rgb(var(--muted))]"
                onClick={onClose}
                aria-label="Fechar menu"
              >
                <X className="size-4" />
              </button>
              <SidebarContent onNavigate={onClose} />
            </motion.aside>
          </>
        ) : null}
      </AnimatePresence>
    </>
  );
}
